import React from 'react';
import { useTranslation } from 'react-i18next';

const AboutPage = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-blue-50">

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-16 animate-fadeIn">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            {t('about.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('about.subtitle')}
          </p>
        </div>

        {/* Mission Section */}
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 mb-12">
          <div className="flex items-start space-x-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="fas fa-bullseye text-blue-600 text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('about.ourMission')}</h2>
              <p className="text-gray-600 leading-relaxed">
                {t('about.missionDesc1')}
              </p>
              <p className="text-gray-600 leading-relaxed mt-6">
                {t('about.missionDesc2')}
              </p>
            </div>
          </div>
        </div>

        {/* Salient Features Section */}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
          {t('about.salientFeatures')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-smile text-yellow-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('about.userFriendly')}</h3>
            <p className="text-gray-600">
              {t('about.userFriendlyDesc')}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-hands-helping text-green-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('about.comprehensiveAssistance')}</h3>
            <p className="text-gray-600">
              {t('about.comprehensiveAssistanceDesc')}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-cogs text-blue-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('about.customizedFeatures')}</h3>
            <p className="text-gray-600">
              {t('about.customizedFeaturesDesc')}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-robot text-purple-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('about.llmChatbot')}</h3>
            <p className="text-gray-600">
              {t('about.llmChatbotDesc')}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-video text-red-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('about.trainingVideos')}</h3>
            <p className="text-gray-600">
              {t('about.trainingVideosDesc')}
            </p>
          </div>
        </div>

        {/* Features to be Added Section */}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
          {t('about.featuresToBeAdded')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-language text-indigo-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('about.multilingualSupport')}</h3>
            <p className="text-gray-600">
              {t('about.multilingualSupportDesc')}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-microphone text-teal-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('about.voiceRecognition')}</h3>
            <p className="text-gray-600">
              {t('about.voiceRecognitionDesc')}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-industry text-orange-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('about.industryCustomization')}</h3>
            <p className="text-gray-600">
              {t('about.industryCustomizationDesc')}
            </p>
          </div>
        </div>

        {/* Contact Section */}
        <div className="max-w-4xl mx-auto text-center bg-white rounded-2xl shadow-lg p-8 mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('about.getInTouchTitle')}</h2>
          <p className="text-gray-600 mb-6">
            {t('about.getInTouchDesc')}
          </p>
          <a href="mailto:chemsafe.iitm@gmail.com"
            className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors duration-300">
            <i className="fas fa-envelope"></i>
            <span>{t('home.email')}</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
